class MediaWithTextSection extends HTMLElement {
  constructor() {
    super();
    this.youTubePlayers = {};
    this.vimeoPlayers = {};
    this.init = this.init.bind(this);
  }

  connectedCallback() {
    document.addEventListener('shopify:section:load', this.onSectionLoad.bind(this));
    if (document.readyState !== 'loading') {
      this.init();
    } else {
      document.addEventListener('DOMContentLoaded', this.init);
    }
  }

  onSectionLoad(event) {
    if (event.target === this) {
      this.init();
    }
  }

  init() {
    this.observeVideos();
    this.setupPlayButtons();
  }

  observeVideos() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const el = entry.target;
        const id = el.dataset.sectionId;
        const type = el.dataset.type;
        const loaded = el.dataset.loaded;

        if (entry.isIntersecting) {
          if (!loaded && type !== 'image') {
            this.loadVideo(el, id, type);
            el.dataset.loaded = 'true';
          } else if (el.dataset.playing === 'false' && type !== 'image') {
            this.play(el, type, id);
          }
        } else {
          if (el.dataset.playing === 'true' && type !== 'image') {
            this.pause(el, type, id);
          }
        }
      });
    }, { threshold: 0.01 });

    this.querySelectorAll('media-hero').forEach(el => observer.observe(el));
  }

  setupPlayButtons() {
    this.querySelectorAll('.media-with-text__play-button').forEach(button => {
      const clone = button.cloneNode(true);
      button.parentNode.replaceChild(clone, button);
      clone.addEventListener('click', () => {
        const id = clone.dataset.sectionId;
        const el = this.querySelector(`media-hero[data-section-id="${id}"]`);
        const type = el?.dataset.type;
        const action = clone.dataset.action;
        
        if (action === 'pause') {
          this.pause(el, type, id);
        } else {
          this.play(el, type, id);
        }
      });
    });
  }

  loadVideo(el, id, type) {
    el.dataset.playing = 'true';
    const btn = this.querySelector(`.media-with-text__play-button[data-section-id="${id}"]`);
    if (btn) btn.dataset.action = 'pause';

    if (type === 'mp4') {
      const video = el.querySelector('video');
      if (video) {
        video.muted = true;
        video.play().catch(console.warn);
        video.addEventListener('play', () => this.updateBtn(id, 'pause'));
        video.addEventListener('pause', () => this.updateBtn(id, 'play'));
      }
    } else if (type === 'youtube') {
      const vid = this.extractYouTubeID(el.dataset.videoId);
      if (!window.YT) {
        const script = document.createElement('script');
        script.src = "https://www.youtube.com/iframe_api";
        document.body.appendChild(script);
        window.onYouTubeIframeAPIReady = () => this.createYouTubePlayer(el, id, vid);
      } else {
        this.createYouTubePlayer(el, id, vid);
      }
    } else if (type === 'vimeo') {
      const vid = this.extractVimeoID(el.dataset.videoId);
      const iframe = document.createElement('iframe');
      iframe.src = `https://player.vimeo.com/video/${vid}?background=0&autoplay=1&muted=1&loop=1&transparent=1`;
      iframe.setAttribute('allow', 'autoplay; fullscreen');
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute('width', '100%');
      iframe.setAttribute('height', '100%');
      iframe.className = 'media-with-text__vimeo-iframe';
      el.innerHTML = '';
      el.appendChild(iframe);
      this.vimeoPlayers[id] = iframe;
    }
  }

  createYouTubePlayer(el, id, vid) {
    const div = document.createElement('div');
    div.style.width = '100%';
    div.style.height = '100%';
    el.appendChild(div);
    
    this.youTubePlayers[id] = new YT.Player(div, {
      videoId: vid,
      width: '100%',
      height: '100%',
      playerVars: {
        autoplay: 1,
        mute: 1,
        rel: 0,
        loop: 1,
        controls: 0,
        modestbranding: 1,
        playsinline: 1,
        playlist: vid
      },
      events: {
        onReady: (event) => {
          event.target.playVideo();
        },
        onStateChange: (event) => {
          if (event.data === YT.PlayerState.PLAYING) {
            this.updateBtn(id, 'pause');
          } else if (event.data === YT.PlayerState.PAUSED) {
            this.updateBtn(id, 'play');
          }
        }
      }
    });
  }

  play(el, type, id) {
    if (!el) return;
    
    el.dataset.playing = 'true';
    this.updateBtn(id, 'pause');
    
    if (type === 'youtube') {
      this.youTubePlayers[id]?.playVideo();
    } else if (type === 'vimeo') {
      this.vimeoPlayers[id]?.contentWindow.postMessage(JSON.stringify({ method: 'play' }), '*');
    } else if (type === 'mp4') {
      const video = el.querySelector('video');
      if (video) video.play();
    }
  }

  pause(el, type, id) {
    if (!el) return;
    
    el.dataset.playing = 'false';
    this.updateBtn(id, 'play');
    
    if (type === 'youtube') {
      this.youTubePlayers[id]?.pauseVideo();
    } else if (type === 'vimeo') {
      this.vimeoPlayers[id]?.contentWindow.postMessage(JSON.stringify({ method: 'pause' }), '*');
    } else if (type === 'mp4') {
      const video = el.querySelector('video');
      if (video) video.pause();
    }
  }

  updateBtn(id, state) {
    const btn = this.querySelector(`.media-with-text__play-button[data-section-id="${id}"]`);
    if (btn) {
      btn.dataset.action = state;
    }
  }

  extractYouTubeID(url) {
    const match = url?.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=))([\w-]{11})/);
    return match?.[1] || '';
  }

  extractVimeoID(url) {
    const match = url?.match(/(?:vimeo\.com\/(?:video\/)?)(\d+)/);
    return match?.[1] || '';
  }
}

// Define the custom element
customElements.define('media-with-text-section', MediaWithTextSection);
  